import { Injectable, Inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import {
  take,
  switchMap,
  retryWhen,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AuthFacade } from './+state/auth.facade';
import { AuthConfig, AUTH_CONFIG } from './config';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private _secureUris: RegExp[];
  private _publicUris: RegExp[];
  private _noRefreshUris: RegExp[];

  constructor(
    private _authFacade: AuthFacade,
    @Inject(AUTH_CONFIG)
    { secureUris, publicUris = [], noRefreshUris = [] }: AuthConfig,
  ) {
    this._secureUris = secureUris.map((u) => new RegExp(u, 'i'));
    this._publicUris = publicUris.map((u) => new RegExp(u, 'i'));
    this._noRefreshUris = noRefreshUris.map((u) => new RegExp(u, 'i'));
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      this._publicUris.some((u) => u.test(req.urlWithParams)) ||
      !this._secureUris.some((u) => u.test(req.urlWithParams))
    ) {
      return next.handle(req);
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const noRefresh = this._noRefreshUris.some((u) =>
      u.test(req.urlWithParams),
    );

    const authData$ = this._authFacade.authDataSnapshot$.pipe(
      take(1),
      withLatestFrom(
        this._authFacade.errorSnapshot$,
        this._authFacade.isLoading$,
      ),
      switchMap(([authData, error, isLoading]) =>
        authData
          ? of(authData)
          : of(undefined).pipe(
              tap(() => {
                if (!error && !isLoading) {
                  // no auth data and no error - means it's initial state
                  this._authFacade.authenticate();
                }
              }),
              switchMap(() => this._authFacade.authData$),
              take(1),
            ),
      ),
    );

    return authData$.pipe(
      switchMap(({ accessToken }) =>
        next.handle(
          req.clone({
            setHeaders: { Authorization: `Bearer ${accessToken}` },
          }),
        ),
      ),
      retryWhen((error$) =>
        error$.pipe(
          mergeMap((error: HttpErrorResponse, attempt) =>
            this.retryFunction(error, attempt, noRefresh),
          ),
        ),
      ),
    );
  }

  retryFunction(error: HttpErrorResponse, attempt: number, noRefresh: boolean) {
    return error.status === 401 && attempt === 0
      ? noRefresh
        ? of(undefined).pipe(
            tap(() => this._authFacade.unauthorizedError()),
            switchMap(() => throwError(error)),
          )
        : of(undefined).pipe(tap(() => this._authFacade.authenticate()))
      : throwError(error);
  }
}
